<script setup lang="ts">
import { api } from '@/services/api';
import { monthYear } from '@/services/date';
import type { Location } from '@/types/location';
import type { Pagination } from '@/types/pagination';
import type { Review } from '@/types/review';
import { onMounted, ref } from 'vue';

const isFirstLoad = ref<boolean>(true);
const reviews = ref<Pagination<Review>>();

const props = defineProps<{
    location: Location;
}>();

async function loadReviews(page: number) {
    try {
        const url = `api/location/${props.location.id}/reviews`;
        const { data } = await api.get<Pagination<Review>>(url, {
            params: { 
                page: page, 
            }
        });

        if (!data) return;

        if (page === 1 || !reviews.value) {
            reviews.value = data;
        } else {
            reviews.value = {
                ...data,
                data: [...(reviews.value.data || []), ...(data.data || [])]
            };
        }
    } catch (errors) {
        // Skip
    }
}


    onMounted(() => {
        loadReviews(1);
    });

</script>
<template>
    <ul>
        <template v-if="isFirstLoad">
            <li v-for="review in (reviews?.data ? reviews.data.slice(0, 2) : [])" :key="review.id">
                <div class="block__reviews__meta">
                    <div class="stars_container">  
                         <template v-for="index in [1, 2, 3, 4, 5]" :key="index">   
                            {{ index <= review.rating ? '' : '' }}                                     
                            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                                <path 
                                    :fill="index <= review.rating ? '#FCCD03' : '#DDDDDD'" 
                                    :stroke="index <= review.rating ? '#FCCD03' : '#DDDDDD'" 
                                    fill-rule="nonzero" 
                                     stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.052.862a.2.2 0 0 1 .36 0l2.464 5.032a.2.2 0 0 0 .15.11l5.516.813a.2.2 0 0 1 .111.34l-3.992 3.919a.2.2 0 0 0-.057.176l.942 5.533a.2.2 0 0 1-.291.21l-4.93-2.611a.2.2 0 0 0-.187 0L4.21 16.996a.2.2 0 0 1-.29-.21l.941-5.534a.2.2 0 0 0-.057-.176L.811 7.157a.2.2 0 0 1 .11-.34l5.516-.813a.2.2 0 0 0 .15-.11L9.052.862Z"/>
                            </svg> 
                        </template>  
                    </div>
                    <span>{{ monthYear.format(new Date(review.created_at))}}</span>
                    <template v-if="review.reservation?.theme">
                        <span>
                            {{ review.reservation.theme.name }}
                        </span>
                    </template>
                </div>
                <p class="block__reviews__text">
                    {{ review.description }}
                </p>
                <p class="block__name">
                    {{ review.reservation?.first_name }} {{ review.reservation?.last_name }} 
                </p>    
            </li>
             <a
                v-if="reviews && reviews.data.length > 2"
                class="link underline"
                @click="isFirstLoad = false"
            >
                Toon meer
            </a>
        </template>
        <template v-else>
            <li v-for="review in reviews?.data" :key="review.id">
                <div class="block__reviews__meta">
                     <div class="stars_container">  
                         <template v-for="index in [1, 2, 3, 4, 5]" :key="index">   
                            {{ index <= review.rating ? '' : '' }}                                     
                            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                                <path 
                                    :fill="index <= review.rating ? '#FCCD03' : '#DDDDDD'" 
                                    :stroke="index <= review.rating ? '#FCCD03' : '#DDDDDD'" 
                                    fill-rule="nonzero" 
                                     stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.052.862a.2.2 0 0 1 .36 0l2.464 5.032a.2.2 0 0 0 .15.11l5.516.813a.2.2 0 0 1 .111.34l-3.992 3.919a.2.2 0 0 0-.057.176l.942 5.533a.2.2 0 0 1-.291.21l-4.93-2.611a.2.2 0 0 0-.187 0L4.21 16.996a.2.2 0 0 1-.29-.21l.941-5.534a.2.2 0 0 0-.057-.176L.811 7.157a.2.2 0 0 1 .11-.34l5.516-.813a.2.2 0 0 0 .15-.11L9.052.862Z"/>
                            </svg> 
                        </template>  
                    </div>
                    <span>{{ monthYear.format(new Date(review.created_at))}}</span>
                    <template v-if="review.reservation?.theme">
                        <span>
                            {{ review.reservation.theme.name }}
                        </span>
                    </template>
                </div>
                <p class="block__reviews__text">
                    {{ review.description }}
                </p>
                <p class="block__name">
                    {{ review.reservation?.first_name }} {{ review.reservation?.last_name }} 
                </p>    
            </li>
              <a
                v-if="reviews && reviews.meta.current_page < reviews.meta.last_page"
                class="link underline"
                @click="loadReviews((reviews.meta.current_page ?? 1) + 1)"
            >
                Toon meer
            </a>
        </template>
    </ul>
</template>